import type { Vec2, OBB } from '../../geo/types';
import type { PolygonIndex } from '../../geo/polygonIndex';
import type { Rng } from '../../core/rng';
import { obbCorners } from '../../geo/polygon';
import { obbOverlap } from '../../geo/sat';
import { polylineLength } from '../../geo/polyline';
import type { Lot, LotSide, LotSource, ReservedFootprint } from './types';
import { LOT, SOURCE_PRIORITY, sampleFrontage, sampleDepth, minDepthFor } from './lotRules';
import { obbFromFrontEdge, obbToAABB, obbsMayMeet } from './lotObb';
import { measureLine, type MeasuredLine } from './measuredLine';
import { createCellGrid, type CellGrid } from './cellGrid';
import { createReservedIndex, type ReservedIndex } from './reservedIndex';

export interface LotGeneratorInput {
  readonly sources: readonly LotSource[];
  readonly buildableIndex: PolygonIndex;
  readonly reserved: readonly ReservedFootprint[];
  readonly rng: Rng;
}

export interface LotGeneratorOutput {
  readonly lots: readonly Lot[];
  readonly diagnostics: LotDiagnostics;
}

function checkNoLotOverlap(obb: OBB, lotHash: CellGrid<Lot>): boolean {
  return !lotHash.some(
    obbToAABB(obb),
    (existing) => obbsMayMeet(obb, existing.obb) && obbOverlap(obb, existing.obb, LOT.overlapTolerance)
  );
}

export type RejectionReason = 'outside_buildable' | 'overlaps_lot' | 'overlaps_reserved' | 'placed';

export interface LotDiagnostics {
  outsideBuildable: number;
  overlapsLot: number;
  overlapsReserved: number;
  placed: number;
  totalAttempts: number;
}

interface FrontEdge {
  frontA: Vec2;
  frontB: Vec2;
  sideDir: Vec2;
}

function computeFrontEdge(line: MeasuredLine, side: LotSide, s: number, frontage: number): FrontEdge {
  const { point: frontA, tangent } = line.pointAt(s);
  const { point: frontB } = line.pointAt(s + frontage);

  /* At the middle of the front, so a widening bank runs as far in at one end as out at the other. */
  const width = line.widthAt(s + frontage / 2);
  const offset = width / 2 + LOT.sourceOffset;

  const sideDir: Vec2 = side === 'left' ? [-tangent[1], tangent[0]] : [tangent[1], -tangent[0]];
  return {
    frontA: [frontA[0] + sideDir[0] * offset, frontA[1] + sideDir[1] * offset],
    frontB: [frontB[0] + sideDir[0] * offset, frontB[1] + sideDir[1] * offset],
    sideDir,
  };
}

function isFrontEdgeInBuildable(edge: FrontEdge, buildableIndex: PolygonIndex): boolean {
  return buildableIndex.contains(edge.frontA) && buildableIndex.contains(edge.frontB);
}

function findMaxValidDepth(
  edge: FrontEdge,
  minDepth: number,
  maxDepth: number,
  buildableIndex: PolygonIndex,
  lotHash: CellGrid<Lot>,
  reserved: ReservedIndex
): number | null {
  const towardSource: Vec2 = [-edge.sideDir[0], -edge.sideDir[1]];
  const fits = (depth: number) => {
    const obb = obbFromFrontEdge(edge.frontA, edge.frontB, depth, towardSource);
    return (
      buildableIndex.obbInside(obb) &&
      checkNoLotOverlap(obb, lotHash) &&
      !reserved.overlaps(obb)
    );
  };

  if (!fits(minDepth)) return null;
  if (fits(maxDepth)) return maxDepth;

  let lo = minDepth;
  let hi = maxDepth;
  while (hi - lo > 0.5) {
    const mid = (lo + hi) / 2;
    if (fits(mid)) {
      lo = mid;
    } else {
      hi = mid;
    }
  }

  return lo;
}

function tryMinFrontageLot(
  source: LotSource,
  line: MeasuredLine,
  side: LotSide,
  s: number,
  minDepth: number,
  lotIndex: number,
  ordinal: number,
  buildableIndex: PolygonIndex,
  lotHash: CellGrid<Lot>,
  reserved: ReservedIndex,
  depthRng: Rng,
  diag?: LotDiagnostics
): Lot | null {
  const minEdge = computeFrontEdge(line, side, s, LOT.shrink.minFrontage);
  if (!isFrontEdgeInBuildable(minEdge, buildableIndex)) return null;

  const tryDepth = sampleDepth(source.kind, depthRng);
  const validDepth = findMaxValidDepth(
    minEdge,
    minDepth,
    tryDepth,
    buildableIndex,
    lotHash,
    reserved
  );
  if (validDepth === null) return null;

  if (diag) diag.totalAttempts++;
  const towardSource: Vec2 = [-minEdge.sideDir[0], -minEdge.sideDir[1]];
  const obb = obbFromFrontEdge(minEdge.frontA, minEdge.frontB, validDepth, towardSource);
  const corners = obbCorners(obb);
  if (diag) diag.placed++;

  return {
    index: lotIndex,
    sourceId: source.id,
    side,
    ordinal,
    kind: 'house',
    obb,
    corners,
    frontage: LOT.shrink.minFrontage,
    depth: validDepth,
    facing: towardSource,
  };
}

function walkSource(
  source: LotSource,
  side: LotSide,
  buildableIndex: PolygonIndex,
  lotHash: CellGrid<Lot>,
  reserved: ReservedIndex,
  rng: Rng,
  startIndex: number,
  diag?: LotDiagnostics
): { lots: Lot[]; nextIndex: number } {
  const lots: Lot[] = [];
  let lotIndex = startIndex;
  /* Draws fork on the lot's place along this side, so lots on other sources never shift them. */
  let ordinal = 0;

  const line = measureLine(source.centerline, source.widths);
  const sourceLen = line.length;
  let s = 0;

  const sourceKind = source.kind;

  while (s < sourceLen - LOT.shrink.minFrontage) {
    let frontage = sampleFrontage(sourceKind, rng.fork(`f${ordinal}`));
    const depth = sampleDepth(sourceKind, rng.fork(`d${ordinal}`));

    if (s + frontage > sourceLen) {
      frontage = sourceLen - s;
      if (frontage < LOT.shrink.minFrontage) break;
    }

    const edge = computeFrontEdge(line, side, s, frontage);

    if (!isFrontEdgeInBuildable(edge, buildableIndex)) {
      if (diag) {
        diag.outsideBuildable++;
        diag.totalAttempts++;
      }
      s += LOT.shrink.advanceStep;
      continue;
    }

    const minDepth = minDepthFor(sourceKind);
    const validDepth = findMaxValidDepth(
      edge,
      minDepth,
      depth,
      buildableIndex,
      lotHash,
      reserved
    );

    let lot: Lot | null = null;
    if (validDepth !== null) {
      if (diag) diag.totalAttempts++;
      const towardSource: Vec2 = [-edge.sideDir[0], -edge.sideDir[1]];
      const obb = obbFromFrontEdge(edge.frontA, edge.frontB, validDepth, towardSource);
      const corners = obbCorners(obb);
      if (diag) diag.placed++;
      lot = {
        index: lotIndex,
        sourceId: source.id,
        side,
        ordinal,
        kind: 'house',
        obb,
        corners,
        frontage,
        depth: validDepth,
        facing: towardSource,
      };
    } else {
      if (diag) {
        diag.outsideBuildable++;
        diag.totalAttempts++;
      }
    }

    if (!lot && frontage > LOT.shrink.minFrontage) {
      lot = tryMinFrontageLot(
        source,
        line,
        side,
        s,
        minDepth,
        lotIndex,
        ordinal,
        buildableIndex,
        lotHash,
        reserved,
        rng.fork(`d2_${ordinal}`),
        diag
      );
    }

    if (lot) {
      lots.push(lot);
      lotHash.insert(lot, obbToAABB(lot.obb));
      s += lot.frontage;
      lotIndex++;
      ordinal++;
    } else {
      s += LOT.shrink.advanceStep;
    }
  }

  return { lots, nextIndex: lotIndex };
}

export function generateLots(input: LotGeneratorInput): LotGeneratorOutput {
  const { sources, buildableIndex, reserved, rng } = input;

  const lotHash = createCellGrid<Lot>(16);
  const reservedIndex = createReservedIndex(reserved);

  const diag: LotDiagnostics = {
    outsideBuildable: 0,
    overlapsLot: 0,
    overlapsReserved: 0,
    placed: 0,
    totalAttempts: 0,
  };

  const sortedSources = [...sources].sort((a, b) => {
    const pa = SOURCE_PRIORITY[a.kind] ?? 10;
    const pb = SOURCE_PRIORITY[b.kind] ?? 10;
    if (pa !== pb) return pa - pb;

    const lenA = polylineLength(a.centerline);
    const lenB = polylineLength(b.centerline);
    if (lenB !== lenA) return lenB - lenA;

    return a.id.localeCompare(b.id);
  });

  const allLots: Lot[] = [];
  let lotIndex = 0;

  for (const source of sortedSources) {
    const sourceRng = rng.fork(`lot:${source.id}`);

    const leftRng = sourceRng.fork('left');
    const leftResult = walkSource(
      source,
      'left',
      buildableIndex,
      lotHash,
      reservedIndex,
      leftRng,
      lotIndex,
      diag
    );
    allLots.push(...leftResult.lots);
    lotIndex = leftResult.nextIndex;

    const rightRng = sourceRng.fork('right');
    const rightResult = walkSource(
      source,
      'right',
      buildableIndex,
      lotHash,
      reservedIndex,
      rightRng,
      lotIndex,
      diag
    );
    allLots.push(...rightResult.lots);
    lotIndex = rightResult.nextIndex;
  }

  return { lots: allLots, diagnostics: diag };
}
